// @/services/auth/index.js
import { keycloakAuthService } from './keycloak';
import { initSessionTimeout } from './sessionTimeout';
import { scmPermissionsService } from '@/services/scm-permissions';
import { cacheManager } from '@/services/app/CacheManager';
import { logoutService } from './logout';

/**
 * Global authentication status tracker to avoid redundant checks
 * Maintains initialization state and caches auth status for performance
 * @type {Object}
 */
export const authState = {
  initialized: false,
  authenticated: null,
  checkPromise: null
};

/**
 * Get cached authentication information from multiple sources
 * Checks sessionStorage, localStorage, and memory state for auth data
 * Implements a multi-layer fallback strategy for reliability
 * 
 * @returns {Object|null} Authentication state object or null if not authenticated
 */
export const getCachedAuthState = () => {
  if (!authState.initialized) {
    // Use CacheManager to get auth state
    const cachedAuth = cacheManager.getAuthState();
    if (cachedAuth) {
      return cachedAuth;
    }

    // Fallback to cross-tab auth
    const crossTabAuth = cacheManager.getCrossTabAuth();
    if (crossTabAuth) {
      return {
        authenticated: true,
        user: crossTabAuth.user || { projectId: crossTabAuth.projectId },
        projectId: crossTabAuth.projectId,
        token: crossTabAuth.token
      };
    }

    return null;
  }

  return {
    authenticated: authState.authenticated,
    user: keycloakAuthService.state.userProfile,
    projectId: keycloakAuthService.state.userProfile?.projectId,
    token: keycloakAuthService.state.token,
    tokenExpiry: keycloakAuthService.state.tokenExpiry
  };
};

/**
 * Cache current user profile for persistence during page reloads
 * Stores profile data in sessionStorage and cross-tab storage
 * 
 * @param {Object} profile - User profile data to cache
 */
export const cacheUserProfile = (profile) => {
  if (!profile) return;

  try {
    // Use CacheManager to save auth state
    cacheManager.saveAuthState({
      authenticated: true,
      userProfile: profile,
      token: keycloakAuthService.state?.token,
      tokenExpiry: keycloakAuthService.state?.tokenExpiry,
      projectId: profile.projectId
    });

    // Also store in cross-tab storage
    storeCrossTabAuth(profile);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error caching user profile:', e);
    }
  }
};

/**
 * Store authentication data for cross-tab sharing
 * Enables synchronized authentication across browser tabs
 * 
 * @param {Object} profile - User profile data
 */
export const storeCrossTabAuth = (profile) => {
  if (!profile) return;

  try {
    // Use CacheManager for cross-tab auth
    const token = keycloakAuthService.state?.token || null;
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Not storing cross-tab auth: missing token');
      }
      return;
    }

    cacheManager.saveCrossTabAuth({
      projectId: profile.projectId,
      userProfile: profile,
      token: token,
      refreshToken: keycloakAuthService.keycloak?.refreshToken,
      idToken: keycloakAuthService.keycloak?.idToken
    });
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error storing cross-tab auth:', e);
    }
  }
};

/**
 * Check authentication status efficiently
 * Handles page reloads, cross-tab authentication, and token validation
 * Initializes session timeout for authenticated users
 * ENHANCED: Now handles session timeout state
 * 
 * @returns {Promise<boolean>} Authentication status
 */
export const checkAuthStatus = async () => {
  // CRITICAL: Check for ALL logout states FIRST
  if (typeof sessionStorage !== 'undefined') {
    const sessionTimeout = sessionStorage.getItem('sessionTimeout') === 'true';
    const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
    const crossTabLogout = sessionStorage.getItem('crossTabLogout') === 'true';
    const onLogoutPage = sessionStorage.getItem('onLogoutPage') === 'true';
    const skipAuth = sessionStorage.getItem('skipAuthProcessing') === 'true';

    if (sessionTimeout || controlledLogout || crossTabLogout || onLogoutPage || skipAuth) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Auth] Auth check skipped - logout state detected:', {
          sessionTimeout,
          controlledLogout,
          crossTabLogout,
          onLogoutPage,
          skipAuth
        });
      }

      // Clear auth state to prevent confusion
      authState.initialized = true;
      authState.authenticated = false;

      // Clear any existing auth check promise
      authState.checkPromise = null;

      return false;
    }
  }

  // Check if we're on the logout page
  if (typeof window !== 'undefined' &&
    (window.location.pathname === '/logout' || window.location.pathname === '/logout/')) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Auth] Auth check skipped - on logout page');
    }

    authState.initialized = true;
    authState.authenticated = false;
    authState.checkPromise = null;

    return false;
  }

  // Check for cached profile first for page reloads
  if (typeof window !== 'undefined') {
    const isPageReload = window.performance?.navigation?.type === 1;

    if (isPageReload) {
      // Try to restore cached profile for faster UI rendering
      const cachedAuth = cacheManager.getAuthState();
      if (cachedAuth && cachedAuth.userProfile && cachedAuth.token) {
        // Force authentication state when we have valid profile and token data
        authState.initialized = true;
        authState.authenticated = true;

        // Set cached profile and token in Keycloak service
        keycloakAuthService.state.authenticated = true;
        keycloakAuthService.state.userProfile = cachedAuth.userProfile;
        keycloakAuthService.state.token = cachedAuth.token;
        keycloakAuthService.state.tokenExpiry = cachedAuth.tokenExpiry;
      }
    }

    // Check for cross-tab authentication
    const crossTabAuth = cacheManager.getCrossTabAuth();
    if (crossTabAuth && crossTabAuth.token) {
      // Preset auth state from cross-tab data
      authState.initialized = true;
      authState.authenticated = true;

      // Update Keycloak service state
      keycloakAuthService.state.authenticated = true;
      keycloakAuthService.state.token = crossTabAuth.token;
      keycloakAuthService.state.userProfile = crossTabAuth.user ||
        { projectId: crossTabAuth.projectId };
    }
  }

  // Use existing promise if available
  if (authState.checkPromise) {
    return authState.checkPromise;
  }

  // Cache the promise to prevent duplicate calls
  authState.checkPromise = keycloakAuthService.initialize()
    .then(async (result) => {
      // Update cache
      authState.initialized = true;
      authState.authenticated = result;

      // Initialize session timeout if authenticated 
      if (result && typeof window !== 'undefined') {
        const sessionTimeout = initSessionTimeout(keycloakAuthService);
        sessionTimeout.initialize();
      }

      // Cache profile if available
      if (result && keycloakAuthService.state.userProfile) {
        cacheUserProfile(keycloakAuthService.state.userProfile);

        // Initialize SCM permissions after authentication
        try {
          await scmPermissionsService.initialize();
          if (process.env.NODE_ENV === 'development') {
            console.log('SCM permissions initialized after authentication');
          }
        } catch (permError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error initializing SCM permissions:', permError);
          }
        }

        // Dispatch auth state change event for UI components
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('authStateChange', {
            detail: {
              type: 'authCheckComplete',
              authenticated: true,
              user: keycloakAuthService.state.userProfile
            }
          }));
        }
      }

      // Clear promise
      authState.checkPromise = null;
      return result;
    })
    .catch(error => {
      // Clear promise on error
      authState.checkPromise = null;
      console.error('Authentication check failed:', error);
      return false;
    });

  return authState.checkPromise;
};

/**
 * Performs back-channel logout with reliable server-side session termination
 * Handles cross-tab notification and comprehensive client-side cleanup
 * 
 * MIGRATED: Now uses the new LogoutService
 * 
 * @returns {Promise<boolean>} Success status
 */
export const backChannelLogout = async () => {
  return logoutService.logout({
    reason: 'user_initiated',
    isInitiator: true
  });
};

/**
 * Comprehensive cleanup of all authentication data from multiple storage locations
 * Ensures complete session termination and prevents auth state leakage
 * 
 * MIGRATED: Now uses CacheManager for complete cleanup
 * 
 * @returns {boolean} Success status
 */
export const completeStorageCleanup = () => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('Performing complete storage cleanup...');
    }

    // Use CacheManager for complete cleanup
    cacheManager.clearAll();

    // Clear SCM permissions
    scmPermissionsService.clearPermissions();

    // Clear Keycloak state
    if (typeof window !== 'undefined' && window.authService && window.authService.keycloak) {
      try {
        const keycloak = window.authService.keycloak;
        keycloak.clearToken();
        keycloak.authenticated = false;
        keycloak.token = null;
        keycloak.refreshToken = null;
        keycloak.idToken = null;
        keycloak.tokenParsed = null;
        keycloak.refreshTokenParsed = null;
        keycloak.idTokenParsed = null;
        keycloak.realmAccess = null;
        keycloak.resourceAccess = null;

        // Also try to reset Keycloak's internal state
        if (typeof keycloak.reset === 'function') {
          keycloak.reset();
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error resetting Keycloak client state:', e);
        }
      }
    }

    // Reset auth service state
    if (typeof window !== 'undefined' && window.authService && window.authService.state) {
      window.authService.state.authenticated = false;
      window.authService.state.token = null;
      window.authService.state.userProfile = null;
      window.authService.state.tokenExpiry = null;
    }

    // Reset global auth state
    if (typeof window !== 'undefined' && window.authState) {
      window.authState.initialized = false;
      window.authState.authenticated = false;
      window.authState.checkPromise = null;
    }

    // Shutdown session timeout if active
    if (typeof window !== 'undefined' && window.authService && window.authService.shutdownSessionTimeout) {
      window.authService.shutdownSessionTimeout();
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Storage cleanup completed');
    }
    return true;
  } catch (error) {
    console.error('Error in complete storage cleanup:', error);
    return false;
  }
};

/**
 * Reset the auth state for testing or force re-authentication
 * Clears authentication state and storage completely
 */
export const resetAuthState = () => {
  authState.initialized = false;
  authState.authenticated = null;
  authState.checkPromise = null;

  // Also reset Keycloak's auth promise if the method exists
  if (keycloakAuthService.clearAuthState) {
    keycloakAuthService.clearAuthState();
  }

  // Clear all auth-related storage
  completeStorageCleanup();
};

// Re-export the Keycloak auth service
export const authService = keycloakAuthService;

// Add session timeout methods to authService
authService.initSessionTimeout = () => {
  if (typeof window !== 'undefined') {
    const sessionTimeout = initSessionTimeout(authService);
    return sessionTimeout.initialize();
  }
  return false;
};

authService.shutdownSessionTimeout = () => {
  if (typeof window !== 'undefined') {
    const { getSessionTimeout } = require('./sessionTimeout');
    const sessionTimeout = getSessionTimeout();
    if (sessionTimeout) {
      return sessionTimeout.shutdown();
    }
  }
  return false;
};

// Add the back-channel logout method
authService.backChannelLogout = backChannelLogout;

// Add explicit storage clear method
authService.clearAuthStorage = completeStorageCleanup;

// Also export the reset function
keycloakAuthService.reset = resetAuthState;

// Export clear storage function 
export const clearAuthStorage = completeStorageCleanup;

export default keycloakAuthService;